import {Translations} from '@aboutyou/i18n';
import {
  FeaturesProvider,
  UserSelectedDiscountProvider,
} from '@aboutyou/logic-components';
import {GlobalErrorBoundary} from '@aboutyou/mlm';
import {ErrorBoundary, UIProvider} from '@aboutyou/ui-components';
import {UserStateProvider} from '@aboutyou/user';
import React, {FC} from 'react';
import {IconsCache} from 'react-lazy-svg';

interface Props extends React.ComponentProps<typeof FeaturesProvider> {
  loadIconSVG: (key: string) => Promise<string | undefined>;
  knownIcons?: IconsCache;
  authToken?: string;
}

export const BaseProvider: FC<Props> = ({
  children,
  headContext,
  loadIconSVG,
  knownIcons,
  i18nConfig,
  routerConfig,
  hostConfig,
  grpcConfig,
  storage,
  featureFlagSetup,
  documentReferrer,
  serverRenderingMode,
  disableTranslationLoader,
  authToken,
}) => {
  const errorBoundaryTexts = getErrorBoundaryTranslations(
    i18nConfig.translations,
  );

  return (
    <GlobalErrorBoundary errorBoundaryTexts={errorBoundaryTexts}>
      <UIProvider loadIconSVG={loadIconSVG} knownIcons={knownIcons}>
        <FeaturesProvider
          disableTranslationLoader={disableTranslationLoader}
          serverRenderingMode={serverRenderingMode}
          documentReferrer={documentReferrer}
          storage={storage}
          headContext={headContext}
          hostConfig={hostConfig}
          i18nConfig={i18nConfig}
          routerConfig={routerConfig}
          grpcConfig={grpcConfig}
          featureFlagSetup={featureFlagSetup}
        >
          <UserStateProvider
            UserSelectedDiscountProvider={UserSelectedDiscountProvider}
            authToken={authToken}
          >
            <ErrorBoundary renderContext="bootstrap_base_provider">
              {children}
            </ErrorBoundary>
          </UserStateProvider>
        </FeaturesProvider>
      </UIProvider>
    </GlobalErrorBoundary>
  );
};

const getErrorBoundaryTranslations = (translations: Partial<Translations>) => {
  const strings: Array<keyof Translations> = [
    'Oh, Sorry!',
    'Our shop is currently not available.',
    'Try again',
    'Start page',
  ];
  return strings.reduce(
    (keys, key) => ({...keys, [key]: translations[key] ?? key}),
    {} as Translations,
  );
};
